<script lang="ts">
	import { Col, Collapse, Container, Row, Text } from '@Shelly/shelly-design-system/index';
	import {
		getBrowserMediaSupport,
		getMediaSettings,
		getStreamStats,
	} from '../../helpers/webrtc-stats';
	import { onDestroy } from 'svelte';
	import { getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import type { WebRTCClient } from '../../helpers/webrtc';

	export let deviceId: string = null;
	export let webRTChandler: WebRTCClient = null;
	export let streamMediaStream: MediaStream = null;

	let videoData = [];
	let browserSupportData = getBrowserMediaSupport();
	let streamData = [];
	let transportData = [];

	let timeout = null;
	let expanded = false;
	let expandedItems: any = {}; //Dynamic expanded items

	$: statusStore = getDeviceStatus(deviceId);
	$: status = $statusStore;

	// Reset data when changing the stream
	$: init(deviceId);

	onDestroy(() => {
		if (timeout) {
			clearTimeout(timeout);
			timeout = null;
		}
	});

	const init = (_device_id) => {
		if (timeout) {
			clearTimeout(timeout);
			timeout = null;
		}
		videoData = [];
		streamData = [];
		transportData = [];
		expandedItems = {};
		if (expanded) {
			loadDynamicData();
		}
	};

	const loadDynamicData = async () => {
		const peerConnection = webRTChandler?.getPeerConnection();

		prepareVideoData(streamMediaStream);
		await prepareStreamData(peerConnection);

		timeout = setTimeout(async () => {
			console.log('==> Stats LOADED');

			loadDynamicData();
		}, 5000);
	};

	const prepareVideoData = (stream: MediaStream) => {
		const info = getMediaSettings(stream);
		if (!info) return (videoData = []);
		let data = [
			{
				title: 'Width',
				value: info.video.width,
			},
			{
				title: 'Height',
				value: info.video.height,
			},
			{
				title: 'Frame Rate',
				value: info.video.frameRate?.toFixed(2),
			},
			{
				title: 'Aspect Ratio',
				value: info.video.aspectRatio?.toFixed(2),
			},
		];
		videoData = data;
	};

	const prepareStreamData = async (stream: RTCPeerConnection) => {
		if (!stream) {
			return;
		}
		let data = await getStreamStats(stream);
		if (!data) return;

		transportData = data.transport;
		streamData = data.stream;
	};

	const handleOnClick = () => {
		expanded = !expanded;
		if (expanded) {
			loadDynamicData();
		} else if (timeout) {
			clearTimeout(timeout);
			timeout = null;
		}
	};
</script>

<Collapse
	id="webrtc-info-stream-information"
	title="Stream information"
	{expanded}
	class="mb-m"
	on:click={handleOnClick}
>
	<Row>
		<Col sm="12" md="4">
			<Container>
				<Text styleType="h2" class="mb-m">Stream</Text>

				{#each streamData as { title, value }}
					<Row class="gap-m pb-xs">
						<Col>
							<Text styleType="body-small" class="text-bold align-self-center mr-s">
								{title}
							</Text>
						</Col>
						<Col>
							{typeof value !== 'undefined' ? value : 'N/A'}
						</Col>
					</Row>
				{/each}
			</Container>
		</Col>
		<Col>
			<Container class="gap-m">
				<Text styleType="h2" class="mb-m">Transport</Text>
				<Row class="gap-m pb-xs">
					<Col>
						<Text styleType="body-small" class="text-bold align-self-center mr-s">
							Device:
						</Text>
					</Col>
					<Col>
						{deviceId} ({status?.online ? 'Online' : 'Offline'})
					</Col>
				</Row>
				{#each transportData as { title, value }}
					<Row class="gap-m pb-xs">
						<Col>
							<Text styleType="body-small" class="text-bold align-self-center mr-s">
								{title}
							</Text>
						</Col>
						<Col>
							{typeof value !== 'undefined' ? value : 'N/A'}
						</Col>
					</Row>
				{/each}
			</Container>
		</Col>
		<Col>
			<Container>
				<Text styleType="h2" class="mb-m">Video info</Text>
				{#each videoData as { title, value }}
					<Row class="gap-m pb-xs">
						<Col class="gap-m">
							<Text styleType="body-small" class="text-bold align-self-center mr-s">
								{title}
							</Text>
						</Col>
						<Col>
							{value ? value : 'N/A'}
						</Col>
					</Row>
				{/each}
			</Container>
		</Col>
	</Row>
	<Container class="mt-m">
		<Collapse
			id="webrtc-info-browser-support"
			title="Browser support"
			expanded={expandedItems?.browser_support}
			on:click={() => {
				expandedItems.browser_support = !expandedItems?.browser_support;
			}}
		>
			{#each browserSupportData as { title, values }}
				<Row>
					<Collapse
						id={`webrtc-info-browser-support-${title}`}
						{title}
						expanded={expandedItems?.[title]}
						on:click={() => {
							expandedItems[title] = !expandedItems?.[title];
						}}
					>
						{#each values as val}
							<Text styleType="body-small" class=" align-self-center mb-s">
								{val}
							</Text>
						{/each}
					</Collapse>
				</Row>
			{/each}
		</Collapse>
	</Container>
</Collapse>
